/**
 * Centralized constants for all custom Properties dictionary keys used across the frontend.
 *
 * Properties dictionaries (Record<string, string>) are attached to various API models (ProductModel,
 * CartLineModel, DataView) to pass custom data from backend handlers to the frontend.
 * This file consolidates all keys to ensure consistency and provide documentation for each property.
 */

// ============================================================================
// PARENT PRODUCT INFORMATION
// ============================================================================
// These properties provide parent product information for variant products.
// Populated by backend handlers:
// - GetProductHandler (AddParentInformationForVariantHandler, Order: 700)
// - GetCartHandler (PopulateParentProductForVariantsHandler, Order: 2550)

/**
 * The ERP number (SKU) of the parent product for a variant.
 * Used to identify and link variant products back to their parent/style product.
 *
 * @context ProductModel.properties, CartLineModel.properties
 * @valueType string
 * @example "ABC-123"
 */
export const PARENT_PRODUCT_NUMBER = "parentProductNumber";
export const PARENT_PRODUCT_TITLE = "parentProductTitle";

/**
 * The image URL/path of the parent product's primary image.
 * Falls back through LargeImagePath -> MediumImagePath -> SmallImagePath.
 * The path is processed through CDN mapping when available.
 *
 * @context ProductModel.properties, CartLineModel.properties
 * @valueType string (URL or relative path)
 * @example "https://cdn.example.com/images/product-123.jpg"
 */
export const PARENT_PRODUCT_THUMBNAIL = "parentProductThumbnail";

/**
 * The canonical URL path to the parent product's detail page.
 * Generated using ICatalogPathBuilder.MakeCanonicalProductUrlPath().
 *
 * @context ProductModel.properties, CartLineModel.properties
 * @valueType string (URL path)
 * @example "/products/parent-product-name"
 */
export const PARENT_PRODUCT_URL = "parentProductUrl";
export const VARIANT_PRODUCT_URL = "variantProductUrl";

/**
 * Flag indicating whether the current product is a variant of a parent/style product.
 * Set to "True" (string) when the product has a StyleParentId.
 *
 * @context ProductModel.properties, CartLineModel.properties
 * @valueType string (boolean as string - "True" or "False")
 * @example "True"
 */
export const IS_VARIANT_PRODUCT = "isVariantProduct";
export const CUSTOM_PART_NUMBER = "customPartNumber";
export const CUSTOM_PRICE = "customPrice";
export const CUSTOM_WEIGHT = "customWeight";
export const CUSTOM_LENGTH = "customLength";
export const CUSTOM_G1 = "customG1";
export const CUSTOM_G2 = "customG2";
/**
 * Days-to-ship value computed by the Rail Configurator for a custom rail line.
 * Persisted as an OrderLine custom property so the dynamic value (e.g. "1-3 days")
 * is shown in cart/review/order/email instead of the product's static "Days to Ship"
 * AttributeValue. Key matches the C# constant Extensions.Constants.ProductPropertiesKeys.CustomDaysToShip.
 *
 * @context CartLineModel.properties, OrderLineModel.properties
 * @valueType string
 * @example "1-3 days"
 */
export const CUSTOM_DAYS_TO_SHIP = "CustomDaysToShip";
export const PARENT_PRODUCT_ID = "customParentProductId";

// ============================================================================
// CUSTOM ATTRIBUTE TYPE PROPERTIES
// ============================================================================
// These properties store custom metadata for attribute types (facets/filters).
// Populated by backend handlers:
// - GetVariantChildCollectionHandler (GetVariantChildCustomAttributeTypeProperty, Order: 1000)
// - GetProductCollectionHandler (AddAttributeTypeHelpPropertiesInProductCollection, Order: 1900)

/**
 * JSON serialized array of custom properties grouped by AttributeType ID.
 * Contains help text, descriptions, or other custom metadata for product attribute types.
 * Data is sourced from dbo.CustomProperty table with ParentTable = 'AttributeType'.
 *
 * @context GetVariantChildCollectionResult.properties, GetProductCollectionResult.properties
 * @valueType string (JSON serialized CustomPropertiesByParentModel[])
 * @jsonStructure
 * [
 *   {
 *     "parentId": "guid-of-attribute-type",
 *     "content": [
 *       { "name": "PropertyName", "value": "PropertyValue" }
 *     ]
 *   }
 * ]
 */
export const CUSTOM_ATTRIBUTE_TYPE_PROPERTIES = "customAttributeTypeProperties";

// ============================================================================
// CUSTOM VARIANT TRAIT PROPERTIES
// ============================================================================
// These properties store custom metadata for variant traits (style traits).
// Populated by backend handler:
// - GetVariantChildCollectionHandler (GetVariantChildCustomTraitProperty, Order: 1050)

/**
 * JSON serialized array of custom properties grouped by StyleTrait ID.
 * Contains help text, descriptions, or other custom metadata for variant traits (e.g., Size, Color).
 * Data is sourced from dbo.CustomProperty table linked to StyleTrait IDs.
 *
 * @context GetVariantChildCollectionResult.properties
 * @valueType string (JSON serialized CustomPropertiesByParentModel[])
 * @jsonStructure
 * [
 *   {
 *     "parentId": "guid-of-style-trait",
 *     "content": [
 *       { "name": "PropertyName", "value": "PropertyValue" }
 *     ]
 *   }
 * ]
 */
export const CUSTOM_VARIANT_TRAIT_PROPERTIES = "customVariantTraitProperties";

// ============================================================================
// VARIANT TRAIT FACETS
// ============================================================================
// These properties store variant trait facet data with product counts for server-side filtering.
// Populated by backend handler:
// - GetVariantChildCollectionHandler (AddVariantTraitFacets, Order: 750)

/**
 * JSON serialized array of variant trait facets with product counts per trait value.
 * Used to render filter options in the variant children grid filter drawer.
 *
 * @context GetVariantChildCollectionResult.properties
 * @valueType string (JSON serialized VariantTraitFacetModel[])
 * @jsonStructure
 * [
 *   {
 *     "styleTraitId": "guid",
 *     "name": "Color",
 *     "sortOrder": 0,
 *     "traitValueFacets": [
 *       { "styleTraitValueId": "guid", "value": "Red", "count": 5 }
 *     ]
 *   }
 * ]
 */
export const VARIANT_TRAIT_FACETS = "variantTraitFacets";

// ============================================================================
// VARIANT ATTRIBUTE FACETS
// ============================================================================
// These properties store variant attribute facet data with counts for server-side filtering.
// Populated by backend handler:
// - GetVariantChildCollectionHandler (AddVariantAttributeFacets, Order: 755)

/**
 * JSON serialized array of variant attribute facets with product counts per attribute value.
 * Used to render filter options in the variant children grid filter drawer for attributes.
 * Counts are computed against all visible (non-restricted) variant children.
 *
 * @context GetVariantChildCollectionResult.Properties
 * @valueType string (JSON serialized List&lt;VariantAttributeFacetDto&gt;)
 * @jsonStructure
 * [
 *     {
 *         "attributeTypeId": "guid-of-attribute-type",
 *         "name": "Material",
 *         "sortOrder": 0,
 *         "attributeValueFacets": [
 *             {"attributeValueId": "guid-of-attribute-value", "value": "Wood", "count": 3 }
 *         ]
 *     }
 * ]
 */
export const VARIANT_ATTRIBUTE_FACETS = "variantAttributeFacets";

// ============================================================================
// PRODUCT INCLUDES/KIT PROPERTIES
// ============================================================================
// These properties indicate whether products have related/included products (kits).
// Populated by backend handlers:
// - GetProductHandler (AddIncludesPropertiesForVariantHandler, Order: 750)
// - GetVariantChildCollectionHandler (AddHasIncludesProductsFlag, Order: 1100)
// - GetVariantChildCollectionHandler (AddIncludesTextCustomProperty, Order: 1150)

/**
 * Flag indicating whether the variant product has related/included products (kit sections).
 * Determined by checking if the product exists in the ProductRelatedProduct table.
 *
 * @context ProductModel.properties (within variant children)
 * @valueType string (lowercase boolean - "true" or "false")
 * @example "true"
 */
export const HAS_INCLUDES_PRODUCTS = "hasIncludesProducts";

/**
 * Custom text describing what is included with the product.
 * Retrieved from dbo.CustomProperty where ParentId matches the StyleClass and Name = 'IncludesText'.
 *
 * @context GetVariantChildCollectionResult.properties
 * @valueType string
 * @example "Includes mounting hardware and instruction manual"
 */
export const INCLUDES_TEXT = "includesText";

export const MAXIMUM_QUANTITY_PER_PRODUCT = "maximumOrderQty";

/**
 * Flag indicating whether the product is a custom product.
 * Used to filter out custom products from the includes selector dropdown.
 *
 * @context ProductModel.properties
 * @valueType string (lowercase boolean - "true" or "false")
 * @example "true"
 */
export const IS_CUSTOM_PRODUCT = "isCustomProduct";

export const DISCONTINUED_BUT_VISIBLE = "discontinuedButVisible";

// ============================================================================
// RAIL CONFIGURATOR PROPERTIES
// ============================================================================
// These properties store pricing, dimensions, and SKU generation data
// for custom rail product configuration. Set on included products via
// the Product entity custom properties (see AddRailConfiguratorProductCustomProperties.sql).

/** Base price in dollars for Case 2 pricing. Formula: CEILING(BasePrice / BaseLength x RailLength x PriceFactor) */
export const BASE_PRICE = "basePrice";

/** Pricing multiplier for Case 2 (standard). Typically 1.1. */
export const PRICE_FACTOR = "priceFactor";

/** Price per millimeter in dollars for SRS products Case 3. Formula: ROUND(MmPrice x RailLength) */
export const MM_PRICE = "mmPrice";

/** Fee in dollars added to catalog price for Case 1 (custom length shorter than base length). */
export const CUT_FEE = "cutFee";

/** Model identifier for part number generation. Examples: SHS20, SRS9X, HSR15. */
export const MODEL = "model";

/** Rail symbol suffix for part number generation. Examples: L, LY, LM(GK), GM. */
export const RAIL_SYMBOL = "railSymbol";

/** Weight per meter in pounds. Weight = WeightPerM x Length x 0.001. */
export const WEIGHT_PER_M = "weightPerM";

/** Base length in mm. Threshold between Case 1 (<=) and Case 2 (>) pricing. */
export const BASE_LENGTH = "baseLength";

/** Product type: "standard" or "srs". Determines Case 2 pricing formula. */
export const PRODUCT_TYPE = "productType";

// ============================================================================
// TYPE FOR CUSTOM PROPERTY KEYS (used in selectors)
// ============================================================================

/**
 * Union type for custom property keys that store JSON serialized CustomPropertiesByParentModel arrays.
 * Used by Redux selectors to parse custom properties from data view properties.
 */
export type CustomPropertyKey =
    | typeof CUSTOM_ATTRIBUTE_TYPE_PROPERTIES
    | typeof CUSTOM_VARIANT_TRAIT_PROPERTIES
    | typeof VARIANT_TRAIT_FACETS
    | typeof INCLUDES_TEXT
    | typeof MAXIMUM_QUANTITY_PER_PRODUCT;
